import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import { Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerticalImage, setIsVerticalImage] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/articles/${slug}`);
        setArticle(response.data);
        
        // Track view
        if (response.data?.id) {
          axios.post(`${API_URL}/api/articles/${response.data.id}/view`).catch(() => {});
        }
        
        // Fetch related articles
        try {
          const relatedResponse = await axios.get(`${API_URL}/api/articles/${slug}/related?limit=3`);
          setRelatedArticles(relatedResponse.data);
        } catch (err) {
          console.log('No related articles found');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        toast.error('Article not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        text: article?.dek,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading article...</p>
          </div>
        </div>
      </>
    );
  }

  if (!article) {
    return (
      <>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold mb-2">Article not found</h2>
            <Link to="/" className="text-primary hover:underline">Return to home</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const currentUrl = window.location.href;
  const ogImage = article.heroImage || 'https://via.placeholder.com/1200x630?text=B%26B+Magazine';
  const ogDescription = article.dek || article.body?.substring(0, 160) || 'Read this story on B&B Magazine';

  return (
    <>
      <Helmet>
        <title>{article.title} | B&B Magazine</title>
        <meta name="description" content={ogDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="B&B Magazine" />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:author" content={article.author || 'B&B Editorial'} />
        {article.category && <meta property="article:section" content={article.category} />}
        {article.tags && article.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": ogDescription,
            "image": ogImage,
            "author": {
              "@type": "Person",
              "name": article.author || 'B&B Editorial'
            },
            "publisher": {
              "@type": "Organization",
              "name": "B&B Magazine",
              "logo": {
                "@type": "ImageObject",
                "url": "https://thebnbmag.com/logo.png"
              }
            },
            "datePublished": article.publishedAt,
            "dateModified": article.updatedAt,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": currentUrl
            },
            "keywords": article.tags?.join(', '),
            "articleSection": article.category
          })}
        </script>
      </Helmet>

      <HeaderNav />

      <main>
        <article>
          {/* Hero Image */}
          <div className={`w-full overflow-hidden bg-[hsl(var(--surface-2))] ${isVerticalImage ? 'aspect-[4/5] md:aspect-[16/12]' : 'aspect-[21/9] md:aspect-[21/7]'}`}>
            <img
              src={article.heroImageUrl}
              alt=""
              className={`w-full h-full ${isVerticalImage ? 'object-contain' : 'object-cover'}`}
              onLoad={(e) => {
                const img = e.target;
                const isPortrait = img.naturalHeight > img.naturalWidth;
                setIsVerticalImage(isPortrait);
              }}
            />
          </div>

          {/* Article Content */}
          <div className="container max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Breadcrumb */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">Home</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="/?filter=articles">Articles</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{article.category}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            {/* Category Badge */}
            {article.category && (
              <Badge className="mb-4 px-2.5 py-1 rounded-full text-xs uppercase tracking-wide bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.3)]">
                {article.category}
              </Badge>
            )}

            {/* Title */}
            <h1 data-testid="article-body" className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
              {article.title}
            </h1>

            {/* Dek (summary) */}
            {article.dek && (
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
                {article.dek}
              </p>
            )}

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
              <span className="font-ui">{article.author || 'B&B Editorial'}</span>
              {article.publishedAt && (
                <>
                  <span>•</span>
                  <span>{formatDate(article.publishedAt)}</span>
                </>
              )}
              {article.readingTime && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {article.readingTime} min read
                  </span>
                </>
              )}
              <button 
                onClick={handleShare}
                data-testid="share-buttons"
                className="ml-auto flex items-center gap-2 text-primary hover:text-primary/80 transition-colors duration-200"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
            </div>

            <Separator className="mb-8" />

            {/* Founders Section */}
            {article.founders && article.founders.length > 0 && (
              <div className="mb-12">
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">Meet the Founders</h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {article.founders.map((founder, index) => (
                    <div key={index} className="flex gap-4 p-6 bg-[hsl(var(--surface-2))] rounded-lg border border-border">
                      {founder.imageUrl && (
                        <img 
                          src={founder.imageUrl} 
                          alt={founder.name}
                          className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-display text-xl font-bold mb-1">{founder.name}</h3>
                        <p className="text-sm text-primary mb-2">{founder.role}</p>
                        {founder.bio && (
                          <p className="text-sm text-muted-foreground leading-relaxed">{founder.bio}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Separator className="mt-8" />
              </div>
            )}

            {/* Article Body */}
            <div className="prose prose-invert max-w-none">
              <div className="text-base md:text-[17px] leading-7 space-y-4 article-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({node, children, ...props}) => {
                      const isFirstParagraph = node.position?.start.line === 1;
                      return (
                        <p className={isFirstParagraph ? 'first-letter:text-5xl first-letter:font-display first-letter:font-bold first-letter:text-primary first-letter:mr-1 first-letter:float-left first-letter:leading-[0.9] mb-4' : 'mb-4'} {...props}>
                          {children}
                        </p>
                      );
                    },
                    a: ({node, children, ...props}) => (
                      <a className="text-primary hover:text-primary/80 underline transition-colors duration-200" target="_blank" rel="noopener noreferrer" {...props}>
                        {children}
                      </a>
                    ),
                    img: ({node, ...props}) => (
                      <img className="w-full rounded-lg my-6" {...props} alt={props.alt || ''} />
                    ),
                    h2: ({node, children, ...props}) => (
                      <h2 className="font-display text-2xl md:text-3xl font-bold mt-8 mb-4" {...props}>
                        {children}
                      </h2>
                    ),
                    h3: ({node, children, ...props}) => (
                      <h3 className="font-display text-xl md:text-2xl font-bold mt-6 mb-3" {...props}>
                        {children}
                      </h3>
                    ),
                    ul: ({node, children, ...props}) => (
                      <ul className="list-disc list-inside space-y-2 my-4" {...props}>
                        {children}
                      </ul>
                    ),
                    ol: ({node, children, ...props}) => (
                      <ol className="list-decimal list-inside space-y-2 my-4" {...props}>
                        {children}
                      </ol>
                    ),
                    blockquote: ({node, children, ...props}) => (
                      <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground" {...props}>
                        {children}
                      </blockquote>
                    ),
                    code: ({node, inline, children, ...props}) => (
                      inline 
                        ? <code className="bg-[hsl(var(--surface-2))] px-1.5 py-0.5 rounded text-sm" {...props}>{children}</code>
                        : <code className="block bg-[hsl(var(--surface-2))] p-4 rounded my-4 overflow-x-auto" {...props}>{children}</code>
                    )
                  }}
                >
                  {article.body}
                </ReactMarkdown>
              </div>
            </div>

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="font-ui">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="max-w-4xl mx-auto mt-16 pt-12 border-t border-border">
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-8">Related Stories</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/articles/${related.slug}`}
                  className="group"
                >
                  <div className="overflow-hidden rounded-lg mb-4">
                    <img
                      src={related.heroImageUrl}
                      alt={related.title}
                      className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <Badge variant="secondary" className="mb-2">{related.category}</Badge>
                  <h3 className="font-display text-lg font-bold line-clamp-2 group-hover:text-primary transition-colors">
                    {related.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {related.dek}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}